import { inject, Injectable } from "@angular/core";
import {
  AmcAnalysesApi,
  AmcAnalysis,
  AmcAnalysisCreation,
  AmcAnalysisStrategy,
  BudgetFlight,
  CampaignType,
  StrategyStateEnum,
} from "@front/m19-api-client";
import { AccountSelectionService, StrategyCache } from "@front/m19-services";
import { ToasterService } from "@front/m19-ui";
import { TranslocoService } from "@jsverse/transloco";
import { saveAs } from "file-saver";
import {
  catchError,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from "rxjs";

export type DeliveredAmcAnalysisStrategy = AmcAnalysisStrategy & {
  firstDeliveryDate: string;
  lastDeliveryDate: string;
  state: StrategyStateEnum;
  budgetFlight?: BudgetFlight;
};

@Injectable({
  providedIn: "root",
})
export class AmcStrategyAnalysisService {
  private readonly amcAnalysesApi = inject(AmcAnalysesApi);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly toaster = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly strategyCache = inject(StrategyCache);

  readonly analyses$: Observable<AmcAnalysis[] | undefined> =
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      filter((am) => !!am.dspAdvertiserId),
      map((am) => ({ dspAdvertiserId: am.dspAdvertiserId!, accountId: am.accountId, marketplace: am.marketplace })),
      switchMap((request) =>
        this.amcAnalysesApi.listAmcAnalyses(request).pipe(
          map((analyses) => [...analyses].sort((a, b) => b.createTime.localeCompare(a.createTime))),
          catchError(() => {
            this.toaster.error(this.translocoService.translate("amc.strategy_analysis_load_error"));
            return of([]);
          }),
          startWith(undefined),
        ),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  readonly dspStrategies$: Observable<DeliveredAmcAnalysisStrategy[] | undefined> =
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      filter((am) => !!am.dspAdvertiserId),
      switchMap((am) =>
        combineLatest([
          this.amcAnalysesApi.listAmcAnalysisStrategies({
            dspAdvertiserId: am.dspAdvertiserId!,
            accountId: am.accountId,
            marketplace: am.marketplace,
          }),
          this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace),
        ]).pipe(
          map(([amcStrategies, strategies]) =>
            amcStrategies
              .filter((s) => s.campaignType === CampaignType.DSP)
              .filter(isDelivered)
              .map((s) => {
                const strategy = strategies.get(s.strategyId);
                return {
                  ...s,
                  name: strategy?.name ?? s.name ?? String(s.strategyId),
                  state: strategy?.state ?? StrategyStateEnum.ENABLED,
                  budgetFlight: strategy?.budgetFlight,
                };
              })
              .sort((a, _) => (a.state === StrategyStateEnum.ENABLED ? -1 : 1)),
          ),
          catchError(() => {
            this.toaster.error(this.translocoService.translate("amc.strategy_analysis_load_error"));
            return of([]);
          }),
          startWith(undefined),
        ),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  getDspStrategy(strategyId: number): Observable<DeliveredAmcAnalysisStrategy | undefined> {
    return this.dspStrategies$.pipe(map((strategies) => strategies?.find((s) => s.strategyId === strategyId)));
  }

  create(creation: AmcAnalysisCreation): Observable<AmcAnalysis> {
    return this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      take(1),
      switchMap((am) =>
        this.amcAnalysesApi.createAmcAnalysis({
          dspAdvertiserId: am.dspAdvertiserId!,
          accountId: am.accountId,
          marketplace: am.marketplace,
          amcAnalysisCreation: creation,
        }),
      ),
      tap({
        next: () => this.toaster.success(this.translocoService.translate("amc.strategy_analysis_created")),
        error: () => this.toaster.error(this.translocoService.translate("amc.strategy_analysis_create_error")),
      }),
    );
  }

  download(analysis: AmcAnalysis): Observable<Blob> {
    return this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      take(1),
      switchMap((am) =>
        combineLatest([
          this.amcAnalysesApi.downloadAmcAnalysis({
            dspAdvertiserId: analysis.dspAdvertiserId,
            analysisId: analysis.analysisId,
          }),
          this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace).pipe(take(1)),
        ]),
      ),
      tap({
        next: ([blob, strategies]) =>
          saveAs(
            blob,
            fileName(
              analysis,
              analysis.strategyIds.map((strategyId) => strategies.get(strategyId)?.name.trim() ?? strategyId).join("_"),
            ),
          ),
        error: () => this.toaster.error(this.translocoService.translate("amc.strategy_analysis_download_error")),
      }),
      map(([blob]) => blob),
    );
  }
}

function fileName(analysis: AmcAnalysis, strategyName: string): string {
  const [firstTemplate, ...otherTemplates] = analysis.requests.map((r) => r.template);
  const templates = otherTemplates.length ? `${firstTemplate}_and_${otherTemplates.length}_others` : firstTemplate;
  return `${strategyName}_${templates}_${analysis.startDate}_${analysis.endDate}.xlsx`;
}

function isDelivered(strategy: AmcAnalysisStrategy): strategy is DeliveredAmcAnalysisStrategy {
  return !!strategy.firstDeliveryDate && !!strategy.lastDeliveryDate;
}
